import React, { memo, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import Loader from "components/loader";
import Typography from "@material-ui/core/Typography";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import Box from "@material-ui/core/Box";
import formatDate from "date-fns/format";
import TabPanel from "components/common/tab-panel";
import { useTranslation } from "react-i18next";
import { getAnnotationColorOptions, NAVIGATION_TABS } from "components/ebook-reader/ebook-reader.utils";
import { useTheme } from "@material-ui/core/styles";


const MAX_ANNOTATION_TEXT_LENGTH = 400;


const AnnotationsTabPanel = memo((props) => {
    const { activeTab, isLoading, items, handleNavItemClick, classes } = props;
    const { t } = useTranslation();
    const tab = useMemo(() => NAVIGATION_TABS.annotation, []);

    const theme = useTheme();
    const colorOptions = useMemo(() => getAnnotationColorOptions(theme), [theme]);
    const getHighlightColorStyle = useCallback(
        (item) => {
            if (item?.color) {
                let newColor = `rgb(${item.color.red},${item.color.green},${item.color.blue}, 0.5)`;
                return { backgroundColor: newColor };
            } else {
                const color = colorOptions.find((option) => option.readerHighlightColorId === 0);
                return { backgroundColor: color.value };
            }
        },
        [colorOptions],
    );

    return (
        <TabPanel key={tab} value={activeTab} index={tab} className={classes.tabPanel}>
            {isLoading && <Loader/>}
            {!items?.length ? (
                <>
                    <Typography noWrap className={classes.emptyMessageTitle}>
                        {t('ebook_no_annotations_title')}
                    </Typography>
                    <Typography noWrap className={classes.emptyMessageSubtitle}>
                        {t('ebook_no_annotations_subtitle')}
                    </Typography>
                </>
            ) : (
                <List>
                    {items.map((item) => (
                        <ListItem
                            button
                            key={item.id}
                            className={classes.navItem}
                            onClick={handleNavItemClick(item.selector)}
                        >
                            <Box className={classes.navItemMetadata}>
                                {!!item?.date && (
                                    <Typography className={classes.navItemSubTitle}>
                                        {formatDate(new Date(item.date), 'MMM d yyyy')}
                                    </Typography>
                                )}
                                {!!item?.page && (
                                    <Typography className={classes.navItemSubTitle}>
                                        {t('generic_format_page', { arg: item.page })}
                                    </Typography>
                                )}
                            </Box>
                            <Typography className={classes.navItemTitle}>
                                {item.title}
                            </Typography>
                            {!!item?.highlight && (
                                <Typography className={classes.navItemSubTitle} style={getHighlightColorStyle(item)}>
                                    {item.highlight.length > MAX_ANNOTATION_TEXT_LENGTH
                                        ? `${item.highlight.substring(0, MAX_ANNOTATION_TEXT_LENGTH)}...`
                                        : item.highlight}
                                </Typography>
                            )}
                            {!!item?.note && (
                                <Typography className={classes.navItemSubTitle}>
                                    {item.note.length > MAX_ANNOTATION_TEXT_LENGTH
                                        ? `${item.note.substring(0, MAX_ANNOTATION_TEXT_LENGTH)}...`
                                        : item.note}
                                </Typography>
                            )}
                        </ListItem>
                    ))}
                </List>
            )}
        </TabPanel>
    )
});

AnnotationsTabPanel.propTypes = {
    activeTab: PropTypes.oneOf(Object.keys(NAVIGATION_TABS)).isRequired,
    isLoading: PropTypes.bool.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        title: PropTypes.string,
        date: PropTypes.string,
        page: PropTypes.number,
        highlight: PropTypes.string,
        note: PropTypes.string,
        color: PropTypes.shape({ red: PropTypes.number, green: PropTypes.number, blue: PropTypes.number }),
        selector: PropTypes.object,
    })).isRequired,
    handleNavItemClick: PropTypes.func.isRequired,
    classes: PropTypes.object.isRequired,
}

export default AnnotationsTabPanel;
